import { useMemo, useState, type CSSProperties } from 'react';
import {
  brands,
  pieces,
  brandById,
  CRAFT_LABEL,
  pieceById,
  piecesForBrand,
  type WatchPiece,
} from '@/data/catalog';
import { brandProfileById } from '@/data/brandsDetail';
import { glossary, GLOSSARY_CATEGORIES } from '@/data/glossary';
import { processSteps, PROCESS_STAGES } from '@/data/process';
import { techTopics, TECH_CATEGORIES } from '@/data/tech';
import { AngleStage } from '@/components/AngleStage';

type Panel = 'piece' | 'brands' | 'glossary' | 'process' | 'tech' | 'about';

export function App() {
  const [activeId, setActiveId] = useState(pieces[0].id);
  const [brandFilter, setBrandFilter] = useState<string | 'all'>('all');
  const [brandFocusId, setBrandFocusId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [panel, setPanel] = useState<Panel>('piece');
  const [glossCat, setGlossCat] = useState<(typeof GLOSSARY_CATEGORIES)[number]['id'] | 'all'>('all');
  const [processStage, setProcessStage] = useState<(typeof PROCESS_STAGES)[number]['id'] | 'all'>('all');
  const [techCat, setTechCat] = useState<(typeof TECH_CATEGORIES)[number]['id'] | 'all'>('all');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pieces.filter((p) => {
      if (brandFilter !== 'all' && p.brandId !== brandFilter) return false;
      if (!q) return true;
      const brand = brandById(p.brandId);
      const hay =
        `${p.name} ${p.nameKo} ${brand?.name ?? ''} ${brand?.nameKo ?? ''} ${p.highlights.join(' ')} ${p.technology.join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [brandFilter, query]);

  const glossFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return glossary.filter((t) => {
      if (glossCat !== 'all' && t.category !== glossCat) return false;
      if (!q) return true;
      return `${t.termKo} ${t.termEn} ${t.body}`.toLowerCase().includes(q);
    });
  }, [glossCat, query]);

  const processFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return processSteps.filter((s) => {
      if (processStage !== 'all' && s.stage !== processStage) return false;
      if (!q) return true;
      return `${s.titleKo} ${s.titleEn} ${s.summary} ${s.details.join(' ')} ${s.historyNote}`.toLowerCase().includes(q);
    });
  }, [processStage, query]);

  const techFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return techTopics.filter((t) => {
      if (techCat !== 'all' && t.category !== techCat) return false;
      if (!q) return true;
      return `${t.titleKo} ${t.titleEn} ${t.principle} ${t.uses.join(' ')} ${t.history}`.toLowerCase().includes(q);
    });
  }, [techCat, query]);

  const brandsFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || panel !== 'brands') return brands;
    return brands.filter((b) => {
      const profile = brandProfileById(b.id);
      const hay = `${b.name} ${b.nameKo} ${b.motto} ${b.summary} ${profile?.strengths.join(' ') ?? ''} ${profile?.character ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, panel]);

  const active: WatchPiece = pieces.find((p) => p.id === activeId) ?? filtered[0] ?? pieces[0];
  const brand = brandById(active.brandId);
  const brandFocus = brandFocusId ? brandById(brandFocusId) : undefined;
  const brandProfile = brandFocusId ? brandProfileById(brandFocusId) : undefined;
  const brandPieces = brandFocusId ? piecesForBrand(brandFocusId) : [];

  const openBrand = (id: string) => {
    setBrandFocusId(id);
    setBrandFilter(id);
    const first = pieces.find((p) => p.brandId === id);
    if (first) setActiveId(first.id);
    setPanel('brands');
  };

  const searchPlaceholder =
    panel === 'glossary'
      ? '용어 검색'
      : panel === 'process'
        ? '공정 검색'
        : panel === 'tech'
          ? '기술 검색'
          : panel === 'brands'
            ? '브랜드 · 강점 검색'
            : '모델 · 브랜드 검색';

  const showroom = panel === 'piece';

  return (
    <div className={showroom ? 'shell' : 'shell is-page'}>
      {showroom && (
        <div className="stage">
          <AngleStage piece={active} />
          <div className="stage-wash" />
        </div>
      )}

      <header className="topbar">
        <button type="button" className="mark" onClick={() => setPanel('piece')} aria-label="쇼룸으로">
          <span className="mark-dial" aria-hidden>
            <span className="mark-bezel" />
            <span className="mark-hand" />
            <span className="mark-hub">H</span>
          </span>
          <div className="mark-copy">
            <p className="mark-title">Horographia</p>
            <p className="mark-sub">
              <span>POL</span>
              <i aria-hidden />
              <span>손목시계 쇼룸</span>
            </p>
          </div>
        </button>
        <nav className="topnav" aria-label="보기">
          {(
            [
              ['piece', '쇼룸', 'Showroom'],
              ['brands', '브랜드', 'Maisons'],
              ['glossary', '용어', 'Lexicon'],
              ['process', '공정', 'Atelier'],
              ['tech', '기술', 'Mechanism'],
              ['about', '안내', 'Notes'],
            ] as const
          ).map(([id, ko, en]) => (
            <button
              key={id}
              type="button"
              className={panel === id ? 'is-on' : ''}
              onClick={() => {
                if (id === 'brands') setBrandFocusId(null);
                if (id === 'piece') setBrandFilter('all');
                setPanel(id);
              }}
            >
              <em>{en}</em>
              <strong>{ko}</strong>
            </button>
          ))}
        </nav>
      </header>

      {showroom && (
      <aside className="rail" aria-label="브랜드 필터">
        <button
          type="button"
          className={brandFilter === 'all' ? 'rail-item is-on' : 'rail-item'}
          onClick={() => {
            setBrandFilter('all');
            setBrandFocusId(null);
          }}
        >
          ALL
        </button>
        {brands.map((b) => (
          <button
            key={b.id}
            type="button"
            className={brandFilter === b.id ? 'rail-item is-on' : 'rail-item'}
            onClick={() => {
              setBrandFilter(b.id);
              setBrandFocusId(null);
              const first = pieces.find((p) => p.brandId === b.id);
              if (first) setActiveId(first.id);
              setPanel('piece');
            }}
            title={b.nameKo}
          >
            {b.name.slice(0, 3).toUpperCase()}
          </button>
        ))}
      </aside>
      )}

      <section className={`dossier dossier-${panel}${showroom ? '' : ' is-page'}`} aria-live="polite">
        {!showroom && (
          <button type="button" className="back-link page-back" onClick={() => setPanel('piece')}>
            ← 쇼룸으로
          </button>
        )}
        {panel === 'piece' && (
          <>
            <p className="dossier-kicker">
              {brand?.nameKo ?? 'Brand'} · {active.year}
            </p>
            <h1 className="dossier-title">{active.nameKo}</h1>
            <p className="dossier-en">{active.name}</p>
            <p className="dossier-body">{active.summary}</p>
            <dl className="specs">
              <div>
                <dt>출시</dt>
                <dd>{active.year}</dd>
              </div>
              <div>
                <dt>출시가</dt>
                <dd>{active.launchPrice}</dd>
              </div>
              <div>
                <dt>현행가</dt>
                <dd>{active.priceBand}</dd>
              </div>
              <div>
                <dt>수제작</dt>
                <dd>
                  {CRAFT_LABEL[active.craft]}
                  <span className="spec-note"> — {active.craftNote}</span>
                </dd>
              </div>
              <div>
                <dt>칼리버</dt>
                <dd>{active.caliber}</dd>
              </div>
              <div>
                <dt>케이스</dt>
                <dd>{active.caseMm}</dd>
              </div>
              <div>
                <dt>브랜드</dt>
                <dd>
                  {brand ? (
                    <button type="button" className="inline-link" onClick={() => openBrand(brand.id)}>
                      {brand.name} ({brand.country}) · 역사·강점
                    </button>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
            </dl>

            <h2 className="dossier-section">특장점</h2>
            <ul className="chips">
              {active.highlights.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>

            <h2 className="dossier-section">기술 · 기능</h2>
            <ul className="tech-list">
              {active.technology.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>

            <p className="dossier-note">가격은 교육용 대략 구간이며 시세·감정·매매 조언이 아닙니다.</p>
          </>
        )}

        {panel === 'brands' && !brandFocus && (
          <>
            <p className="dossier-kicker">Maison index</p>
            <h1 className="dossier-title">브랜드</h1>
            <p className="dossier-body">
              각 메종을 누르면 역사·강점·이정표·쇼룸 피스가 나옵니다. 교육용이며 시세·진품 감정이 아닙니다.
            </p>
            <ul className="brand-list">
              {brandsFiltered.map((b) => {
                const profile = brandProfileById(b.id);
                return (
                <li key={b.id}>
                  <button type="button" onClick={() => openBrand(b.id)}>
                    <strong>{b.nameKo}</strong>
                    <span>
                      {b.name} · {b.founded} · {b.country}
                    </span>
                    <em>{b.motto}</em>
                    {profile && (
                      <span className="brand-teaser">{profile.strengths[0]}</span>
                    )}
                  </button>
                </li>
                );
              })}
              {brandsFiltered.length === 0 && <li className="gloss-empty">검색 결과 없음</li>}
            </ul>
          </>
        )}

        {panel === 'brands' && brandFocus && brandProfile && (
          <>
            <button type="button" className="back-link" onClick={() => setBrandFocusId(null)}>
              ← 브랜드 목록
            </button>
            <p className="dossier-kicker">
              {brandFocus.founded} · {brandProfile.hq}
            </p>
            <h1 className="dossier-title">{brandFocus.nameKo}</h1>
            <p className="dossier-en">{brandFocus.name}</p>
            <p className="dossier-body">{brandFocus.summary}</p>
            <p className="brand-group">{brandProfile.groupNote}</p>
            <p className="brand-character">{brandProfile.character}</p>

            <h2 className="dossier-section">강점</h2>
            <ul className="tech-list">
              {brandProfile.strengths.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>

            <h2 className="dossier-section">역사</h2>
            <ul className="brand-history">
              {brandProfile.history.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>

            <h2 className="dossier-section">이정표</h2>
            <ul className="milestone-list">
              {brandProfile.milestones.map((m) => (
                <li key={`${m.year}-${m.titleKo}`}>
                  <span className="milestone-year">{m.year}</span>
                  <div>
                    <strong>{m.titleKo}</strong>
                    <p>{m.body}</p>
                  </div>
                </li>
              ))}
            </ul>

            <h2 className="dossier-section">시그니처</h2>
            <ul className="chips">
              {brandFocus.signature.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>

            {brandPieces.length > 0 && (
              <>
                <h2 className="dossier-section">쇼룸 피스</h2>
                <div className="learn-related">
                  {brandPieces.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className="learn-chip"
                      onClick={() => {
                        setActiveId(p.id);
                        setPanel('piece');
                      }}
                    >
                      {p.nameKo}
                    </button>
                  ))}
                </div>
              </>
            )}

            <p className="dossier-credit">{brandProfile.watchOut}</p>
          </>
        )}

        {panel === 'glossary' && (
          <>
            <p className="dossier-kicker">Lexicon</p>
            <h1 className="dossier-title">시계 용어</h1>
            <p className="dossier-body">
              케이스·다이얼·무브먼트·기능·마감을 역할·읽는 법·대표 사례까지 풀어 정리했습니다. 하단 검색으로도 찾을 수
              있습니다.
            </p>
            <div className="gloss-cats" role="tablist" aria-label="용어 분류">
              <button
                type="button"
                className={glossCat === 'all' ? 'is-on' : ''}
                onClick={() => setGlossCat('all')}
              >
                전체
              </button>
              {GLOSSARY_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={glossCat === c.id ? 'is-on' : ''}
                  onClick={() => setGlossCat(c.id)}
                >
                  {c.labelKo}
                </button>
              ))}
            </div>
            <ul className="gloss-list">
              {glossFiltered.map((t) => (
                <li key={t.id}>
                  <div className="gloss-head">
                    <strong>{t.termKo}</strong>
                    <span>{t.termEn}</span>
                  </div>
                  <p>{t.body}</p>
                </li>
              ))}
              {glossFiltered.length === 0 && <li className="gloss-empty">검색 결과 없음</li>}
            </ul>
          </>
        )}

        {panel === 'process' && (
          <>
            <p className="dossier-kicker">Manufacture flow</p>
            <h1 className="dossier-title">제작 공정</h1>
            <p className="dossier-body">
              설계부터 출하까지 일반적인 손목시계 공정입니다. 브랜드·모델마다 순서와 깊이는 다릅니다. 교육용 요약입니다.
            </p>
            <div className="gloss-cats" role="tablist" aria-label="공정 단계">
              <button
                type="button"
                className={processStage === 'all' ? 'is-on' : ''}
                onClick={() => setProcessStage('all')}
              >
                전체
              </button>
              {PROCESS_STAGES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={processStage === c.id ? 'is-on' : ''}
                  onClick={() => setProcessStage(c.id)}
                >
                  {c.labelKo}
                </button>
              ))}
            </div>
            <ol className="learn-list">
              {processFiltered.map((s) => (
                <li key={s.id}>
                  <div className="learn-head">
                    <span className="learn-num">{s.order}</span>
                    <div>
                      <strong>{s.titleKo}</strong>
                      <span>{s.titleEn}</span>
                    </div>
                  </div>
                  <p className="learn-summary">{s.summary}</p>
                  <ul className="learn-bullets">
                    {s.details.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                  <p className="learn-history">
                    <em>역사</em> {s.historyNote}
                  </p>
                </li>
              ))}
              {processFiltered.length === 0 && <li className="gloss-empty">검색 결과 없음</li>}
            </ol>
          </>
        )}

        {panel === 'tech' && (
          <>
            <p className="dossier-kicker">Principles</p>
            <h1 className="dossier-title">기술 · 원리</h1>
            <p className="dossier-body">
              시간 분할·동력·방수·컴플리케이션·소재의 원리와 쓰임, 짧은 역사입니다. 쇼룸 피스와 연결해 볼 수 있습니다.
            </p>
            <div className="gloss-cats" role="tablist" aria-label="기술 분류">
              <button type="button" className={techCat === 'all' ? 'is-on' : ''} onClick={() => setTechCat('all')}>
                전체
              </button>
              {TECH_CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={techCat === c.id ? 'is-on' : ''}
                  onClick={() => setTechCat(c.id)}
                >
                  {c.labelKo}
                </button>
              ))}
            </div>
            <ul className="learn-list learn-list-tech">
              {techFiltered.map((t) => (
                <li key={t.id}>
                  <div className="learn-head">
                    <div>
                      <strong>{t.titleKo}</strong>
                      <span>{t.titleEn}</span>
                    </div>
                  </div>
                  <p className="learn-label">원리</p>
                  <p className="learn-summary">{t.principle}</p>
                  <p className="learn-label">쓰임</p>
                  <ul className="learn-bullets">
                    {t.uses.map((u) => (
                      <li key={u}>{u}</li>
                    ))}
                  </ul>
                  <p className="learn-history">
                    <em>역사</em> {t.history}
                  </p>
                  {t.relatedPieceIds.length > 0 && (
                    <div className="learn-related">
                      <span>관련 피스</span>
                      {t.relatedPieceIds.map((id) => {
                        const p = pieceById(id);
                        if (!p) return null;
                        return (
                          <button
                            key={id}
                            type="button"
                            className="learn-chip"
                            onClick={() => {
                              setActiveId(id);
                              setPanel('piece');
                            }}
                          >
                            {p.nameKo}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </li>
              ))}
              {techFiltered.length === 0 && <li className="gloss-empty">검색 결과 없음</li>}
            </ul>
          </>
        )}

        {panel === 'about' && (
          <>
            <p className="dossier-kicker">POL</p>
            <h1 className="dossier-title">안내</h1>
            <p className="dossier-body">
              Horographia는 세계적으로 알려진 손목시계와 브랜드를 한국어로 훑어보는 정적 쇼룸입니다. 화면 중앙
              사진은 정면·사선·측면 시점으로 돌릴 수 있습니다.
            </p>
            <ul className="about-list">
              <li>쇼룸: 출시 연도 · 출시 당시 정가 스케일 · 현행 대략 정가 · 특장점 · 기술/기능 · 수제작 성격</li>
              <li>브랜드: 메종별 역사 · 강점 · 이정표 · 쇼룸 피스</li>
              <li>용어 · 공정 · 기술: 어휘와 제작·원리 (교육용 요약)</li>
              <li>가격은 기록·인용이 불완전한 교육용 스케일입니다. 시세·투자·감정 조언이 아닙니다</li>
            </ul>
          </>
        )}
      </section>

      {showroom && (
      <footer className="deck">
        <label className="search">
          <span className="sr-only">검색</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            autoComplete="off"
          />
        </label>
        <div className="deck-scroll" role="listbox" aria-label="피스 목록">
          {filtered.map((p) => {
            const on = p.id === active.id;
            return (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={on}
                className={on ? 'deck-card is-on' : 'deck-card'}
                onClick={() => {
                  setActiveId(p.id);
                  setPanel('piece');
                }}
                style={
                  {
                    '--metal': p.metal,
                    '--dial': p.dial,
                    '--accent': p.accent,
                  } as CSSProperties
                }
              >
                <img className="deck-thumb" src={p.image.src} alt="" loading="lazy" />
                <i className="deck-swatch" aria-hidden />
                <span className="deck-name">{p.nameKo}</span>
                <span className="deck-meta">{brandById(p.brandId)?.name}</span>
              </button>
            );
          })}
          {filtered.length === 0 && <p className="deck-empty">검색 결과 없음</p>}
        </div>
      </footer>
      )}

      {!showroom && (
        <div className="page-search">
          <label className="search">
            <span className="sr-only">검색</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              autoComplete="off"
            />
          </label>
        </div>
      )}
    </div>
  );
}
